import React, { Component } from 'react';
import propTypes from 'prop-types';
import { titleize, humanize } from 'underscore.string';
import { Copy, Info } from 'lucide-react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import { getLocaleText } from 'utils/localization/agGridLocale';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
  Checkbox,
  Button,
  TooltipTrigger,
  TooltipProvider,
  Tooltip,
  TooltipContent,
} from '@cargomatic/ui';

import { withTranslation } from 'react-i18next';
import { snakify } from '../../../utils/localization/utils';
import { findManyUsers } from '../../../utils/UserWebAPIUtils';
import LoadingCircle from '../../ui/LoadingCircle';

export const ROLES = {
  admin: {
    description: 'Godmode Engaged.',
  },
  internal_account_administrator: {
    description: 'Internal Account Administrator',
  },
  integration_manager: {
    description: 'Integrations Manager',
  },
  accessorial_manager: {
    description: 'Accessorial Manager',
  },
  finance: {
    description: 'Accounting adjustments after invoice',
  },
  'finance-admin': {
    description: 'Manual stripe transfers',
  },
  locations_manager: {
    description: 'Manager of locations',
  },
  locations_admin: {
    description: 'Administrator of locations',
  },
  tariff: {
    description: 'Can Update Customer Tariffs',
  },
  'accounting-admin': {
    description: 'Can set accounting holds',
  },
  'message-admin': {
    description: 'Administrator of Messaging',
  },
  'message-manager': {
    description: 'Manager of Messaging',
  },
  'shipper-lane-pricing-manager': {
    description: 'Manager of Shipper Pricing for Rates',
  },
  'carrier-lane-pricing-manager': {
    description: 'Manager of Carrier Pricing for Rates',
  },
  'message-user': {
    description: 'Limited User of Messaging',
  },
  'message-group-user': {
    description: 'Can create group messages',
  },
  'filter-creator': {
    description: 'Create and share filters',
  },
  'trip-planner-admin': {
    description: 'Create create and apply trips',
  },
  'order-template-admin': {
    description: 'Create order templates',
  },
  support: {
    description: 'Can perform support related features',
  },
  'sales-rep': {
    description: 'Can be a sales rep on an account',
  },
  'carrier-compliance': {
    description: 'Can authorize or deauthorize carriers',
  },
  'aci-zones-admin': {
    description: 'Can update ACI zones',
  },
  'multi-stop-booking': {
    description: 'Can book shipments with a variable number of stops',
  },
  'metrics-admin': {
    description: 'Can export data across metric scorecards',
  },
  'account-admin': {
    description: 'Can administer account mapping/routing',
  },
  'bulk-pricing-manager': {
    description: 'Manage pricing tools',
  },
  'carrier-bulk-auditor': {
    description: 'Manage bulk audit tools',
  },
  customer_manager: {
    description:
      'Manage creation and updates of shipper, carrier and driver users',
  },
  'shipper-onboarding': {
    description:
      'Can edit any shipper account billing addresses above normal admin users',
  },
  'uv-report-manager': {
    description: 'Can manage all UV reports',
  },
  'reference-numbers-admin': {
    description: 'Can update system generated reference numbers',
  },
  'accessorials-admin': {
    description:
      'Can force approvals of accessorial requests regardless of missing documents',
  },
  'billing-manager-admin': {
    description: 'Can manage invoice generation settings',
  },
  billing_status_admin: {
    description: 'Ability to Override any billing status',
  },
  billing_status_manager: {
    description: 'Ability to Override Pending review status to Ready to Bill',
  },
  billing_status_user: {
    description:
      'Ability to Override Ready To Bill status  to Pending Review status',
  },
  'shipment-closure-admin': {
    description: 'close shipments without completing stops',
  },
  bridge_code_manager: {
    description: 'Can Edit and Delete Market Exceptions on Shipments',
  },
};

const ROLE_TOOLTIPS = {
  billing_status_admin: 'BILLING_STATUS_ADMIN_TOOLTIP',
  billing_status_manager: 'BILLING_STATUS_MANAGER_TOOLTIP',
  billing_status_user: 'BILLING_STATUS_USER_TOOLTIP',
  'shipment-closure-admin': 'SHIPMENT_CLOSURE_ADMIN_TOOLTIP',
};

class RolesPermissionsModal extends Component {
  static propTypes = {
    userIds: propTypes.array.isRequired,
    show: propTypes.bool.isRequired,
    onHide: propTypes.func.isRequired,
    onSave: propTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      users: [],
      loading: true,
    };
  }

  componentDidMount() {
    // Add event listener for select all
    document.addEventListener('selectAllRoles', this.handleToggleSelectAll);
    this.fetchUsers();
  }

  componentDidUpdate(prevProps) {
    // Fetch users when userIds change
    if (
      prevProps.userIds !== this.props.userIds ||
      (prevProps.show !== this.props.show && this.props.show)
    ) {
      this.fetchUsers();
    }
  }

  componentWillUnmount() {
    // Clean up event listener
    document.removeEventListener('selectAllRoles', this.handleToggleSelectAll);
  }

  async fetchUsers() {
    const { userIds } = this.props;

    if (!userIds || userIds.length === 0) {
      this.setState({ users: [], loading: false });
      return;
    }

    this.setState({ loading: true });

    try {
      const response = await findManyUsers(userIds);

      // Check different possible response structures
      let users = [];
      if (response.entity && response.entity.items) {
        users = response.entity.items;
      } else if (response.entity && response.entity.data) {
        users = response.entity.data;
      } else if (response.entity && Array.isArray(response.entity)) {
        users = response.entity;
      } else if (Array.isArray(response)) {
        users = response;
      } else {
        console.error('Unexpected response structure:', response);
        this.setState({ users: [], loading: false });
        return;
      }

      const processedUsers = users.map((u) => {
        const first_name = (u.first_name || '').trim();
        const last_name = (u.last_name || '').trim();
        const name = `${first_name} ${last_name}`.trim();
        return {
          ...u,
          first_name,
          last_name,
          name,
        };
      });

      this.setState({ users: processedUsers, loading: false });
    } catch (error) {
      console.error('Failed to fetch users:', error);
      this.setState({ users: [], loading: false });
    }
  }

  mapRole = (role) => {
    const { t } = this.props;

    return humanize(titleize(t(snakify(role))));
  };

  hasRole(user, role) {
    return user.roles.indexOf(role) > -1;
  }

  handleToggleSelectAll = (e) => {
    const { role } = e.detail;
    const allUsersHaveRole = this.state.users.every((user) =>
      this.hasRole(user, role),
    );

    this.setState((prevState) => ({
      users: prevState.users.map((user) => {
        const currentRoles = [...user.roles];
        if (allUsersHaveRole) {
          // Remove role from all users
          const roleIndex = currentRoles.findIndex((r) => r === role);
          return {
            ...user,
            roles: [
              ...currentRoles.slice(0, roleIndex),
              ...currentRoles.slice(roleIndex + 1),
            ],
          };
        } else {
          // Add role to all users if they don't have it
          if (!this.hasRole(user, role)) {
            return {
              ...user,
              roles: [...currentRoles, role],
            };
          }
          return user;
        }
      }),
    }));
  };

  handleUserRoleChange(user, role, checked) {
    this.setState((prevState) => {
      const { users: currentUsers } = prevState;
      const index = currentUsers.findIndex((u) => u._id === user._id);

      let roles;
      const currentRoles = [...user.roles];
      if (checked) {
        roles = [...currentRoles, role];
      } else {
        const roleIndex = currentRoles.findIndex((r) => r === role);
        roles = [
          ...currentRoles.slice(0, roleIndex),
          ...currentRoles.slice(roleIndex + 1),
        ];
      }

      const users = [
        ...currentUsers.slice(0, index),
        { ...user, roles },
        ...currentUsers.slice(index + 1),
      ];

      return { users };
    });
  }

  handleApplyToFollowingUsers(user) {
    this.setState((prevState) => {
      const { users: currentUsers } = prevState;
      const roles = [...user.roles];

      const index = currentUsers.findIndex((u) => u._id === user._id);

      const users = [
        ...currentUsers.slice(0, index).map((u) => ({ ...u, roles })),
        { ...currentUsers[index] },
        ...currentUsers.slice(index + 1).map((u) => ({ ...u, roles })),
      ];
      return {
        users,
      };
    });
  }

  render() {
    const { show, onHide, onSave, t } = this.props;
    const { loading, users: _users } = this.state;

    const defaultColDef = {
      sortable: false,
      filter: false,
      resizable: true,
      cellStyle: {
        padding: '4px 8px',
        display: 'flex',
        alignItems: 'center',
        height: '100%',
      },
    };

    const columnDefs = [
      {
        headerName: 'Name',
        field: 'name',
        pinned: 'left',
        cellRenderer: (params) => {
          return (
            <div className="flex items-center h-full">
              <span
                className="text-sm font-medium"
                data-testid={`user-name-${params.data._id}`}
              >
                {params.value}
              </span>
            </div>
          );
        },
      },
      {
        headerName: t('SELECT_ALL'),
        field: 'selectAll',
        pinned: 'left',
        width: 100,
        headerComponent: () => {
          return (
            <div className="h-full flex items-center">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger className="flex items-center space-x-1">
                    <span>Select All</span>
                    <Info className="w-4 h-4" />
                  </TooltipTrigger>

                  <TooltipContent>
                    <p>This will select all roles for the target user.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          );
        },
        cellRenderer: (params) => {
          const user = this.state.users.find((u) => u._id === params.data._id);
          if (!user) return null;

          const allRolesSelected = Object.keys(ROLES).every((role) =>
            this.hasRole(user, role),
          );
          const someRolesSelected = Object.keys(ROLES).some((role) =>
            this.hasRole(user, role),
          );

          const indeterminate = !allRolesSelected && someRolesSelected;
          const checked = indeterminate ? 'indeterminate' : allRolesSelected;
          return (
            <div className="flex items-center h-full">
              <Checkbox
                checked={checked}
                indeterminate={indeterminate ? 'true' : undefined}
                onCheckedChange={(checked) => {
                  this.setState((prevState) => ({
                    users: prevState.users.map((u) =>
                      u._id === user._id
                        ? { ...u, roles: checked ? Object.keys(ROLES) : [] }
                        : u,
                    ),
                  }));
                }}
                data-testid={`select-all-${user._id}`}
              />
            </div>
          );
        },
      },
      ...Object.keys(ROLES)
        .sort()
        .map((role) => ({
          headerName: this.mapRole(role),
          field: role,
          headerComponent: () => {
            return (
              <div className="h-full flex items-center">
                <TooltipProvider>
                  <Tooltip disabled={!ROLE_TOOLTIPS[role]}>
                    <TooltipTrigger className="flex items-center space-x-1">
                      <span>{this.mapRole(role)}</span>
                      {ROLE_TOOLTIPS[role] && <Info className="w-4 h-4" />}
                    </TooltipTrigger>
                    {ROLE_TOOLTIPS[role] && (
                      <TooltipContent>
                        <p>{t(ROLE_TOOLTIPS[role])}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
            );
          },
          cellRenderer: (params) => {
            const user = this.state.users.find(
              (u) => u._id === params.data._id,
            );
            if (!user) return null;
            return (
              <div className="flex items-center h-full">
                <Checkbox
                  checked={this.hasRole(user, role)}
                  onCheckedChange={(checked) => {
                    this.handleUserRoleChange(user, role, checked);
                  }}
                  data-testid={`role-${role}-${user._id}`}
                />
              </div>
            );
          },
        })),
      {
        headerName: t('ACTIONS'),
        field: 'actions',
        pinned: 'right',
        cellRenderer: (params) => {
          const user = this.state.users.find((u) => u._id === params.data._id);
          if (!user) return null;

          return (
            <div className="flex items-center h-full">
              <Button
                size="icon"
                variant="icon"
                onClick={() => this.handleApplyToFollowingUsers(user)}
                title={t('APPLY_TO_FOLLOWING_USERS')}
                data-testid={`copy-roles-${user._id}`}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          );
        },
      },
    ];

    const rowData = this.state.users.map((user) => ({
      _id: user._id,
      name: user.name,
    }));

    return (
      <Dialog
        open={show}
        onOpenChange={onHide}
        className="z-50"
        data-testid="roles-permissions-modal"
      >
        <DialogContent className="max-w-none h-screen flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle data-testid="roles-permissions-title">
              {t('ROLES_AND_PERMISSIONS')}
            </DialogTitle>
            <DialogDescription data-testid="roles-permissions-description">
              {t('ROLES_AND_PERMISSIONS_TIP')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <LoadingCircle />
              </div>
            ) : (
              <div className="h-full">
                <div
                  className="ag-theme-balham h-full"
                  data-testid="roles-permissions-grid"
                >
                  <AgGridReact
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    rowData={rowData}
                    suppressRowClickSelection
                    enableCellTextSelection
                    localeText={getLocaleText()}
                    rowHeight={32}
                    headerHeight={40}
                    domLayout="normal"
                    suppressContextMenu
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex-shrink-0">
            <div className="flex items-center justify-end gap-4">
              <Button
                onClick={() => onSave(this.state.users)}
                data-testid="save-roles-button"
                disabled={loading}
              >
                {titleize(t('SAVE'))}
              </Button>
              <Button
                variant="secondary"
                onClick={() => onHide()}
                data-testid="cancel-roles-button"
              >
                {titleize(t('CANCEL'))}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
}

export default withTranslation()(RolesPermissionsModal);
